import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leadApi } from '../services/leadApi';
import type { LeadFormData } from '../validators/leadSchema';
import type { LeadOwnershipCountsFilters } from '../types/crm.types';

// Query keys
export const leadKeys = {
  all: ['leads'] as const,
  lists: () => [...leadKeys.all, 'list'] as const,
  // TODO: Replace any with typed filter params.
  list: (filters: Record<string, any> = {}) => [...leadKeys.lists(), filters] as const,
  details: () => [...leadKeys.all, 'detail'] as const,
  detail: (id: string, params?: Record<string, any>) => {
    const key = params && Object.keys(params).length > 0
      ? [...leadKeys.details(), id, params]
      : [...leadKeys.details(), id];
    return key as readonly unknown[];
  },
  kanban: () => [...leadKeys.all, 'kanban'] as const,
  ownershipCounts: (filters: Record<string, unknown> = {}) =>
    [...leadKeys.all, 'ownership-counts', filters] as const,
};

/**
 * Hook to fetch leads with optional filters and pagination
 */
export function useLeads(params?: {
  page?: number;
  per_page?: number;
  search?: string;
  lead_status?: string;
  pipeline_id?: string;
  stage_id?: string;
  assigned_to?: string;
  customer_lifecycle_stage?: 'lead' | 'customer' | 'former_customer';
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  deleted?: 'only' | 'with';
}) {
  return useQuery({
    queryKey: leadKeys.list(params),
    queryFn: () => leadApi.getLeads(params),
  });
}

/**
 * Hook to fetch single lead
 */
export function useLead(id: string, params?: { include_deleted?: boolean }) {
  const keyParams = params && Object.keys(params).length > 0 ? params : undefined;

  return useQuery({
    queryKey: leadKeys.detail(id, keyParams),
    queryFn: () => leadApi.getLead(id, params),
    enabled: !!id,
  });
}

/**
 * Hook to fetch kanban data, optionally scoped to one assignee (#701)
 */
export function useLeadsKanban(pipelineId?: string, assignedTo?: string) {
  return useQuery({
    queryKey: [...leadKeys.kanban(), pipelineId ?? null, assignedTo ?? null] as const,
    queryFn: () => leadApi.getKanban(pipelineId, assignedTo),
    // No enabled check - backend will use default pipeline if pipelineId is undefined
  });
}

/**
 * Hook to fetch ownership bucket counts (#701). Pass the same non-ownership
 * filters the list uses so the badges describe what is on screen.
 */
export function useLeadOwnershipCounts(filters?: LeadOwnershipCountsFilters) {
  return useQuery({
    queryKey: leadKeys.ownershipCounts(filters as Record<string, unknown> | undefined),
    queryFn: () => leadApi.getOwnershipCounts(filters),
  });
}

/**
 * Hook to create lead
 */
export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LeadFormData) => leadApi.createLead(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: [...leadKeys.all, 'ownership-counts'] });
    },
  });
}

/**
 * Hook to update lead
 */
export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LeadFormData> }) =>
      leadApi.updateLead(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: [...leadKeys.all, 'ownership-counts'] });
    },
  });
}

/**
 * Hook to delete lead
 */
export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => leadApi.deleteLead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: [...leadKeys.all, 'ownership-counts'] });
    },
  });
}

/**
 * Hook to move lead to different stage
 */
export function useMoveLeadToStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      stageId,
      disposition,
    }: {
      id: string;
      stageId: string;
      // #717 — required by the backend when the target stage is type='lost'.
      disposition?: { lost_reason: string; lost_notes?: string };
    }) => leadApi.moveToStage(id, stageId, disposition),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: [...leadKeys.all, 'ownership-counts'] });
    },
  });
}

/**
 * Hook to mark lead as won
 */
export function useMarkLeadAsWon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      id, 
      won_amount, 
      won_date 
    }: { 
      id: string; 
      won_amount: number;
      won_date?: string;
    }) => leadApi.markAsWon(id, { won_amount, won_date }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: [...leadKeys.all, 'ownership-counts'] });
    },
  });
}

/**
 * Hook to mark lead as lost
 */
export function useMarkLeadAsLost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      lost_reason,
      lost_notes
    }: {
      id: string;
      lost_reason: string;
      lost_notes?: string;
    }) => leadApi.markAsLost(id, { lost_reason, lost_notes }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: [...leadKeys.all, 'ownership-counts'] });
    },
  });
}

/**
 * #712 — reopen a closed deal. Same invalidation set as won/lost: the deal
 * changes stage, so every list, the kanban, and the ownership counts move.
 */
export function useReopenLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      leadApi.reopen(id, reason ? { reason } : undefined),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: [...leadKeys.all, 'ownership-counts'] });
    },
  });
}

/**
 * Hook to add stage change comment
 */
export function useAddLeadStageComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, stageId, comment }: { id: string; stageId: string; comment: string }) =>
      leadApi.addStageComment(id, { stage_id: stageId, comment }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.id) });
    },
  });
}
